/**
 * U11 (user-reported) simplifies the rule this module used to implement:
 *
 *   "even if row is selected - the outline for changes cells should still be
 *   orange, the fill should just change to the blue selected item
 *   fill/background - same rule for edit mode and a edited cell."
 *
 * W7.8 built this module around a CONTEST: fill is the meaning channel, and
 * selection takes it only where the meaning channel is unused, falling back
 * to a ring (which flipped colour, `--energy` <-> `--primary`) where it is
 * contested. That flip is exactly what U11 asks to remove — a changed/pending
 * cell's ring stayed orange only until you selected it, at which point the
 * one signal that told you "this cell has meaning" disappeared.
 *
 * THE RULE NOW: meaning ALWAYS owns the outline; selection ALWAYS owns the
 * fill. There is no contest left to resolve:
 *
 *   - A meaningful cell (a Data Compare diff, a pending edit) keeps its
 *     orange `--energy` ring whether or not it is selected. The ring is a
 *     plain function of "is this cell meaningful?" alone — draw it whenever
 *     that's true, full stop. No consumer branches on selection to choose
 *     the ring's colour anymore, so this module has nothing left to export
 *     for it beyond the one constant class string every grid shares.
 *   - Selection always owns the fill. A selected cell's background is the
 *     blue selection wash — even one that also carries meaning. An
 *     unselected meaningful cell keeps its own orange wash; that's the ONLY
 *     place the meaning wash still shows, since selection overrides it the
 *     moment the cell is selected.
 *
 * `resolveCellFill` is what survives of W7.8's three-way
 * `resolveSelectionAffordance` once the ring is factored out into an
 * unconditional constant: fill is still a function of both inputs (selection
 * wins over meaning, meaning wins over nothing), but it is no longer coupled
 * to what the ring does.
 *
 * Verified legible (real render, both themes — see the task report for the
 * full numbers): the orange ring reads clearly against the blue selected
 * fill, and a selected-meaningful cell stays visually distinct from a
 * selected-ordinary one purely by virtue of the ring being present.
 */

/**
 * Which wash a cell's background should show.
 *
 * - `'none'`      — not selected, no meaning to show either.
 * - `'meaning'`   — not selected, but the cell carries meaning (changed /
 *                   pending) — the cell's own wash (e.g. `--energy-soft`,
 *                   `--energy`/7) applies.
 * - `'selection'` — selected. Always wins, regardless of meaning — the
 *                   caller applies the blue selection wash. The meaning
 *                   RING (drawn separately, unconditionally, whenever the
 *                   cell is meaningful) is what keeps the meaning signal
 *                   visible once the fill goes blue.
 */
export type CellFillAffordance = 'none' | 'meaning' | 'selection';

/**
 * Resolve which wash a cell's background should carry.
 *
 * @param isSelected   whether selection currently covers this cell (row- or
 *                     cell-scoped — the caller decides what "selected" means
 *                     on its surface).
 * @param isMeaningful whether the cell carries meaning: changed, pending,
 *                     deleted, in error. The caller draws the meaning
 *                     OUTLINE unconditionally whenever this is true —
 *                     independent of this function's result.
 */
export function resolveCellFill(isSelected: boolean, isMeaningful: boolean): CellFillAffordance {
  if (isSelected) return 'selection';
  if (isMeaningful) return 'meaning';
  return 'none';
}

/**
 * The orange meaning outline, shared by all three grids
 * (`DataCompareResultsGrid`'s diff cells, `ResultsGrid`/`TableExplorerGrid`'s
 * pending-edit cells). Unconditional: a meaningful cell draws this ring
 * whether or not it is selected — that's the whole point of U11.
 *
 * `--energy`, never `--primary`: this wave's accent system reserves orange
 * for "work in flight / this differs" and blue for structure and selection.
 * Under the old contested rule this ring flipped to `ring-primary` while
 * selected; U11 removes that flip, so the colour is now a plain constant.
 *
 * `ring-inset` (not an outset ring or an `outline`) because these are table
 * cells: an outset ring would paint over the neighbouring cell and be
 * clipped by the scroll container at the grid's edges.
 *
 * `ring-1` (not `ring-2`): U5 thinned Data Compare's version of this ring
 * from 2px to 1px after a dark-theme legibility complaint; U11 unifies all
 * three grids on that same weight rather than reintroducing a heavier ring
 * on `ResultsGrid`/`TableExplorerGrid` that never got the same review.
 */
export const MEANING_OUTLINE_CLASS = 'ring-1 ring-inset ring-energy';
